const count = (value) => Number(value || 0);
const timestamp = (value) =>
  value?.toMillis?.() || (value ? new Date(value).getTime() : 0);
const STUCK_AFTER_MS = 4 * 60 * 1000;

export const getProcessingReceipt = (source = {}, now = Date.now()) => {
  const receipt = source.processingReceipt;
  const branches = receipt?.branches || {};
  const saved = count(
    branches.timeline?.savedCount ??
      (source.extractionSummary?.savedRecordCount ||
        source.metadata?.captureFirst?.timelineEntryIds?.length),
  );
  const suggested = count(
    branches.facts?.suggestedCount ??
      (source.careKnowledgeSuggestionSummary?.itemCount ||
        source.suggestedCareKnowledgeItems?.length),
  );
  const pointers = count(
    source.metadata?.careSourceProcessing?.timelinePointerCount ||
      source.metadata?.careSourceProcessing?.timelinePointerEntryIds?.length,
  );
  const context =
    source.sourceContext ||
    source.sourceMetadata?.sourceContext ||
    source.metadata?.source?.sourceContext ||
    "timeline";
  const started = timestamp(
    source.metadata?.captureFirst?.processingStartedAt ||
      source.createdAt ||
      source.capturedAt,
  );
  const identifiedCapture = Boolean(
    (source.id && source.childId) ||
    source.metadata?.captureFirst?.enabled ||
    receipt,
  );
  const queued =
    !receipt &&
    ["captured", "organizing"].includes(source.status) &&
    started > now - STUCK_AFTER_MS;
  const legacyStalled =
    !receipt &&
    started > 0 &&
    ["captured", "organizing"].includes(source.status) &&
    !queued;
  const expired =
    legacyStalled ||
    (receipt?.status === "running" && timestamp(receipt.leaseUntil) <= now);
  const running = queued || (receipt?.status === "running" && !expired);
  const metadata = source.sourceMetadata || source.metadata?.source || {};
  const timelineExpected =
    identifiedCapture &&
    context === "timeline" &&
    !source.documentFindings &&
    !pointers &&
    !source.pendingTimelineReview &&
    metadata.sourceUseCase !== "import_history" &&
    !source.extractionResult?.emailNeedsDateCandidates?.length &&
    !["awaiting_review", "skipped"].includes(branches.timeline?.status);
  const missingTimeline = timelineExpected && !saved && !running;
  const error = expired
    ? "Processing stopped before it finished."
    : receipt?.error?.message ||
      (identifiedCapture &&
      ["extraction_failed", "failed"].includes(source.status)
        ? source.extractionError?.message || "Processing did not finish."
        : "") ||
      (missingTimeline
        ? "No Timeline entries were saved for this capture."
        : "");
  const failed = Boolean(
    error ||
    ["failed", "partial_failure"].includes(receipt?.status) ||
    source.failedTimelineEntries?.length,
  );
  const unknownFailure =
    failed &&
    !running &&
    branches.timeline?.status !== "failed" &&
    branches.facts?.status !== "failed";
  const replayAllowed = !["changed", "withdrawn"].includes(
    source.sourceLifecycle?.state,
  );
  return {
    sourceState: source.sourceLifecycle?.state || null,
    status: expired || missingTimeline ? "failed" : receipt?.status || "",
    failed,
    saved,
    suggested,
    profileSuggested: count(
      branches.facts?.profileSuggestedCount ||
        source.profileSuggestionSummary?.suggestionCount,
    ),
    error,
    running,
    retryTimeline:
      replayAllowed &&
      timelineExpected &&
      (missingTimeline ||
        unknownFailure ||
        branches.timeline?.status === "failed" ||
        source.failedTimelineEntries?.length > 0 ||
        expired),
    retryFacts:
      replayAllowed &&
      (branches.facts?.status === "failed" || (expired && saved > 0)),
  };
};

export const getReprocessOptions = (source = {}, branch = "timeline") => {
  const textStatus =
    source.textExtractionStatus ||
    source.sourceMetadata?.textExtractionStatus ||
    source.metadata?.source?.textExtractionStatus;
  const voiceStatus =
    source.transcriptionStatus ||
    source.voiceTranscriptionStatus ||
    source.sourceMetadata?.transcriptionStatus;
  // Reading/transcription must recover through the existing preparation pipeline.
  if (textStatus === "failed" || voiceStatus === "failed")
    return { force: true };
  return { reprocess: true, branch };
};
